import React, { useState, useEffect } from 'react';
import {css} from 'glamor';
import { useQuery, useMutation } from "@apollo/client";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";

import * as theme from "../../../theme";
import { withInstallation } from "../../../components/InstallationContext";
import { FlashMessage } from "../../../components/FlashMessage";
import { StatusPill } from "../../../components/StatusPill";
import { Button } from "../../../components/Button";
import { Container, CardContainer } from "../../../components/Container";
import { Left, Right } from "../../../components/Header";
import { Well } from "../../../components/Well";

import { H1, H2 } from "../../../components/Typography";
import { Table, THead, TBody } from "../../../components/Table";
import { Loading, LoadingError } from "../../../components/LoadingFork";
import { listUsersQuery, updateUserMutation } from "./queries";
import authManager, {PERMISSIONS} from "../../../lib/authManager";
import { HollowButton } from "../../../components/Button";
import trashIcon from "../../../icons/trash.svg";
import {PermissionOnly} from "../../../components/AdminOnly";

function Header(props) {
  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "stretch",
      })}
      {...props}
    />
  );
}

function Status({ isEnabled }) {
  return (
    <td>
      <div
        className={css({
          display: "flex",
          justifyContent: "stretch",
          flexDirection: "column",
        })}
      >
        <StatusPill flavour={isEnabled ? "success" : "light"}>
          {isEnabled ? "Active" : "Inactive"}
        </StatusPill>
      </div>
    </td>
  );
}

const rowStyle = css({
  "> td": {
    verticalAlign: "middle",
    height: 60,
    borderBottom: `1px solid ${theme.gray10}`,
    transition: "all .2s linear",
  },
  "> td:first-child": { paddingLeft: 10 },
  "> td:last-child": { paddingRight: 10 },
  ":hover > td": {
    background: theme.gray10,
    cursor: "pointer",
  },
});

export function roleFromUserRoles(userRoles, installationId, isRootAdmin) {
  if (isRootAdmin) return "Root Administrator";

  const userRole = userRoles?.find((userRole) => {
    return userRole.installationId === installationId;
  });

  return userRole?.role?.label || userRole?.role?.name || "None";
}

function UserRow({ user, onClick, onDelete, onReactivate, showDeleteButton, installationId }) {
  const { name, email, phone, userRoles, isRootAdmin } = user;

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete(user);
  };

  const handleReactivateClick = (e) => {
    e.stopPropagation();
    onReactivate(user);
  };

  return (
    <tr key={user.id} className={rowStyle}>
      <td onClick={onClick}>{name}</td>
      <td onClick={onClick}>{roleFromUserRoles(userRoles, installationId, isRootAdmin)}</td>
      <td onClick={onClick}>{email}</td>
      <td onClick={onClick}>{phone}</td>
      <td onClick={onClick}>
        <Status isEnabled={user.isEnabled} />
      </td>
      {showDeleteButton && (
        <td onClick={(e) => e.stopPropagation()}>
          <div
            className={css({
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            })}
          >
            {user.isEnabled ? (
              <img
                src={trashIcon}
                alt="Delete"
                onClick={handleDeleteClick}
                className={css({
                  cursor: "pointer",
                  width: "18px",
                  height: "18px",
                  opacity: 0.7,
                  transition: "opacity 0.2s",
                  ":hover": {
                    opacity: 1,
                  },
                })}
              />
            ) : (
              <HollowButton
                size="small"
                onClick={handleReactivateClick}
                className={css({
                  padding: "4px 12px",
                })}
              >
                Reactivate
              </HollowButton>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

export function filterInactiveUsers(users) {
  return users.filter((user) => user?.isEnabled);
}

export function sortUsersByEnabledStatus(users) {
  return [...users].sort((a, b) => {
    // Sort enabled (true) users first, then disabled (false) users
    if (a.isEnabled === b.isEnabled) return 0;
    return a.isEnabled ? -1 : 1;
  });
}

function UsersList({ match, installation, location, history }) {
  const {
    data = {},
    loading,
    error,
    refetch,
  } = useQuery(listUsersQuery, {
    fetchPolicy: "no-cache",
  });
  const [users, setUsers] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [userToReactivate, setUserToReactivate] = useState(null);
  const [updateUserEnabled, { loading: deleting }] = useMutation(updateUserMutation);

  const [reactivateUser, { loading: reactivating }] = useMutation(updateUserMutation);

  useEffect(() => {
    if (!loading) {
      if (authManager.isRootAdmin()) {
        const allUsers = data.allUsers || [];
        setUsers(sortUsersByEnabledStatus(allUsers));
      } else {
        setUsers(filterInactiveUsers(data.allUsers || []));
      }
    }
  }, [data, loading]);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    if (!userToDelete.id || !userToDelete.name || !userToDelete.email) {
      console.error("Invalid user data:", userToDelete);
      alert("Cannot delete user: Missing required fields");
      return;
    }

    try {
      await updateUserEnabled({
        variables: {
          id: userToDelete.id,
          name: userToDelete.name,
          email: userToDelete.email,
          phone: userToDelete.phone || " ",
          isEnabled: false,
        },
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      refetch();
    } catch (error) {
      console.error("Error deleting user:", error);
      console.error("Error details:", {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      });

      const errorMessage =
        error.graphQLErrors?.[0]?.message || error.message || "Unknown error";
      alert(`Error deleting user: ${errorMessage}`);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleReactivateClick = (user) => {
    setUserToReactivate(user);
    setReactivateDialogOpen(true);
  };

  const handleReactivateConfirm = async () => {
    if (!userToReactivate) return;

    if (
      !userToReactivate.id ||
      !userToReactivate.name ||
      !userToReactivate.email
    ) {
      console.error("Invalid user data:", userToReactivate);
      alert("Cannot reactivate user: Missing required fields");
      return;
    }

    try {
      await reactivateUser({
        variables: {
          id: userToReactivate.id,
          name: userToReactivate.name,
          email: userToReactivate.email,
          phone: userToReactivate.phone || " ",
          isEnabled: true,
        },
      });
      setReactivateDialogOpen(false);
      setUserToReactivate(null);
      refetch();
    } catch (error) {
      console.error("Error reactivating user:", error);
      console.error("Error details:", {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      });

      const errorMessage =
        error.graphQLErrors?.[0]?.message || error.message || "Unknown error";
      alert(`Error reactivating user: ${errorMessage}`);
    }
  };

  const handleReactivateCancel = () => {
    setReactivateDialogOpen(false);
    setUserToReactivate(null);
  };

  const isRootAdmin = authManager.isRootAdmin();
  const canManageUsers = authManager.hasPermission(
    PERMISSIONS.USERS_MANAGE,
    installation.id
  );
  const showDeleteButton = isRootAdmin || canManageUsers;

  return (
    <div>
      <CardContainer>
        <Container size="large" spaceAbove="small" spaceBelow="small">
          <Header>
            <Left>
              <H1>Users</H1>
            </Left>
            <Right>
              {!loading && users.length > 0 && (
                <PermissionOnly permissions={PERMISSIONS.USERS_MANAGE}>
                  <Button
                    to={`${match.url}/new`}
                    style={{ "background-color": "#3127B8" }}
                  >
                    New user
                  </Button>
                </PermissionOnly>
              )}
            </Right>
          </Header>
        </Container>
        <Container spaceBelow="large">
          <FlashMessage locationState={location.state} />
          {loading && <Loading />}
          {error && <LoadingError error={error} />}
          {!loading && users.length === 0 && (
            <Well>
              <div>
                <H2 spaceBelow="large">No users available</H2>
                <Button spaceBelow="none" to={`${match.url}/new`}>
                  Create a new user now
                </Button>
              </div>
            </Well>
          )}
          {users.length > 0 && (
            <Table>
              <THead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  {showDeleteButton && <th></th>}
                </tr>
              </THead>
              <TBody>
                {users.map((user) => {
                  return (
                    <UserRow
                      key={user.id}
                      user={user}
                      onClick={() => {
                        if (!canManageUsers) {
                          return;
                        }

                        history.push(`${match.url}/${user.id}`);
                      }}
                      onDelete={handleDeleteClick}
                      onReactivate={handleReactivateClick}
                      showDeleteButton={showDeleteButton}
                      installationId={installation.id}
                    />
                  );
                })}
              </TBody>
            </Table>
          )}
        </Container>
      </CardContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete {userToDelete?.name}? This will
            remove the user account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <HollowButton size="small" onClick={handleDeleteCancel}>
            Cancel
          </HollowButton>
          <HollowButton
            size="small"
            flavour="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            autoFocus
          >
            {deleting ? "Deleting..." : "Delete"}
          </HollowButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reactivateDialogOpen}
        onClose={handleReactivateCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to reactivate {userToReactivate?.name}? This
            will enable the user account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <HollowButton size="small" onClick={handleReactivateCancel}>
            Cancel
          </HollowButton>
          <HollowButton
            size="small"
            flavour="primary"
            onClick={handleReactivateConfirm}
            disabled={reactivating}
            autoFocus
          >
            {reactivating ? "Reactivating..." : "Reactivate"}
          </HollowButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default withInstallation(UsersList)
